'use client';

import { useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { AssetLogListItem } from '@evimo/types/api/goldenRecord/find-asset-logs/find-asset-logs.response.dto';
import { AssetLogsService } from '~/app/services/goldenRecord/asset-logs.service';

type UseAssetLogsOptions = {
  organizationId: string;
  assetId: string;
  enabled?: boolean;
};

export type AssetLogsFilters = {
  from?: string;
  to?: string;
  isTreated?: boolean;
};

/** First page size when opening Logs. */
export const ASSET_LOGS_INITIAL_PAGE_SIZE = 20;
/** Subsequent infinite-scroll page size. */
export const ASSET_LOGS_NEXT_PAGE_SIZE = 50;

export function assetLogsPageLimit(pageParam: string | undefined): number {
  return pageParam === undefined
    ? ASSET_LOGS_INITIAL_PAGE_SIZE
    : ASSET_LOGS_NEXT_PAGE_SIZE;
}

export type UseAssetLogsResult = {
  logs: AssetLogListItem[];
  /** Size of the first page (for UI/docs); later pages use ASSET_LOGS_NEXT_PAGE_SIZE. */
  pageSize: number;
  isLoading: boolean;
  isError: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  hasNextPage: boolean;
  filters: AssetLogsFilters;
  setFilters: (filters: AssetLogsFilters) => void;
  fetchNextPage: () => Promise<void>;
};

export function useAssetLogs({
  organizationId,
  assetId,
  enabled = true,
}: UseAssetLogsOptions): UseAssetLogsResult {
  const [filters, setFilters] = useState<AssetLogsFilters>({ isTreated: true });

  const query = useInfiniteQuery({
    queryKey: ['asset-logs', organizationId, assetId, filters],
    queryFn: ({ pageParam }) =>
      AssetLogsService.findPaginated(organizationId, assetId, {
        limit: assetLogsPageLimit(pageParam),
        cursor: pageParam,
        ...filters,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage && lastPage.nextCursor
        ? lastPage.nextCursor
        : undefined,
    enabled: enabled && !!organizationId && !!assetId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const logs = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data?.pages],
  );

  return {
    logs,
    pageSize: ASSET_LOGS_INITIAL_PAGE_SIZE,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    hasNextPage: Boolean(query.hasNextPage),
    filters,
    setFilters,
    fetchNextPage: async () => {
      if (query.isFetchingNextPage || query.isFetchNextPageError) {
        return;
      }
      await query.fetchNextPage();
    },
  };
}
